import { HEAT_ZONE_RADIUS_PX, LOOT_TIER_COLORS, type DropLootTier } from './FpsDropHeatmap.js';

const PANEL_SIZE = 168;
const CANVAS_SIZE = 160;
const PAD = 4;
/** Bot squad dots fade out beyond this world distance from the player. */
const BOT_DOT_FADE_START = 55;
const BOT_DOT_FADE_END = 120;

export type FpsMinimapPoi = { name: string; x: number; z: number };

export type FpsMinimapHeatZone = {
  name: string;
  x: number;
  z: number;
  tier: DropLootTier;
  color?: string;
};

export type FpsMinimapOptions = {
  worldHalfExtent: number;
  pois?: FpsMinimapPoi[];
};

export type FpsMinimapStorm = {
  stormRadius?: number;
  safeRadius: number;
  cx: number;
  cz: number;
  colorblind?: boolean;
  nextRadius?: number;
  nextCx?: number;
  nextCz?: number;
  safePulse?: number;
};

export type FpsMinimapMarker = {
  x: number;
  z: number;
  color: string;
  label?: string;
  knocked?: boolean;
  dead?: boolean;
  /** Render as a filled circle (squad bot dots). */
  dot?: boolean;
};

export type FpsMinimapBusRoute = {
  startX: number;
  startZ: number;
  endX: number;
  endZ: number;
  busX?: number;
  busZ?: number;
};

/**
 * Fixed top-right minimap: arena outline, grid, POIs, player heading.
 */
export class FpsMinimap {
  private readonly mount: HTMLElement;
  private readonly root: HTMLDivElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly responsiveStyle: HTMLStyleElement;
  private readonly worldHalfExtent: number;
  private readonly pois: FpsMinimapPoi[];
  private readonly trail: { x: number; z: number }[] = [];
  private readonly maxTrail = 40;
  private readonly baseShadow = '0 6px 28px rgba(0,0,0,0.48),inset 0 1px 0 rgba(255,255,255,0.1),0 0 18px rgba(46,230,255,0.12)';
  private mapClickEnabled = false;
  private stormPulseUntil = 0;
  private hoveredPoi: string | null = null;
  private readonly poiTooltip: HTMLDivElement;
  private onMapClick?: (x: number, z: number) => void;

  constructor(mount: HTMLElement, options: FpsMinimapOptions) {
    this.mount = mount;
    this.worldHalfExtent = Math.max(1e-6, options.worldHalfExtent);
    this.pois = options.pois ?? [];

    this.root = document.createElement('div');
    this.root.className = 'fps-minimap';
    this.root.style.cssText = [
      'position:fixed',
      'top:calc(env(safe-area-inset-top, 0px) + 12px)',
      'right:calc(env(safe-area-inset-right, 0px) + 12px)',
      'z-index:40',
      `width:${PANEL_SIZE}px`,
      `height:${PANEL_SIZE}px`,
      'box-sizing:border-box',
      'padding:6px',
      'border-radius:var(--fps-radius,14px)',
      'background:var(--fps-panel-bg,linear-gradient(145deg,rgba(18,24,42,0.72),rgba(8,12,22,0.88)))',
      'backdrop-filter:blur(14px)',
      '-webkit-backdrop-filter:blur(14px)',
      'border:1px solid var(--fps-accent-dim,rgba(46,230,255,0.38))',
      'box-shadow:var(--fps-shadow,0 6px 28px rgba(0,0,0,0.48)),inset 0 1px 0 rgba(255,255,255,0.1),0 0 18px rgba(46,230,255,0.12)',
      'transition:transform 0.2s ease,box-shadow 0.2s ease',
      'font-family:var(--fps-font,system-ui,sans-serif)',
    ].join(';');

    const compassRose = document.createElement('div');
    compassRose.className = 'fps-minimap-compass';
    compassRose.style.cssText = [
      'position:absolute',
      'top:2px',
      'left:50%',
      'transform:translateX(-50%)',
      'z-index:2',
      'display:flex',
      'flex-direction:column',
      'align-items:center',
      'pointer-events:none',
    ].join(';');
    const compassN = document.createElement('span');
    compassN.textContent = 'N';
    compassN.style.cssText = [
      'font:900 10px var(--fps-font,system-ui,sans-serif)',
      'color:var(--fps-accent,#2ee6ff)',
      'letter-spacing:0.08em',
      'text-shadow:0 0 8px rgba(46,230,255,0.55),0 1px 3px rgba(0,0,0,0.85)',
      'line-height:1',
    ].join(';');
    const compassTick = document.createElement('span');
    compassTick.style.cssText = [
      'width:0',
      'height:0',
      'border-left:4px solid transparent',
      'border-right:4px solid transparent',
      'border-bottom:5px solid var(--fps-accent,#2ee6ff)',
      'filter:drop-shadow(0 0 4px rgba(46,230,255,0.45))',
      'margin-top:1px',
    ].join(';');
    compassRose.appendChild(compassN);
    compassRose.appendChild(compassTick);
    this.root.appendChild(compassRose);

    this.responsiveStyle = document.createElement('style');
    this.responsiveStyle.textContent = `
.fps-minimap{width:clamp(118px,22vw,168px)!important;height:clamp(118px,22vw,168px)!important}
@media (max-width:720px){.fps-minimap{top:calc(env(safe-area-inset-top,0px) + 8px)!important;right:calc(env(safe-area-inset-right,0px) + 8px)!important;width:clamp(112px,28vw,142px)!important;height:clamp(112px,28vw,142px)!important;padding:4px!important;border-radius:12px!important}}
@media (max-width:520px),(max-height:520px){.fps-minimap{width:112px!important;height:112px!important;padding:3px!important;border-radius:10px!important}}
@media (max-height:420px){.fps-minimap{width:96px!important;height:96px!important}}
@media (prefers-reduced-motion:reduce){.fps-minimap{transition:none!important}}
`;
    document.head.appendChild(this.responsiveStyle);

    this.canvas = document.createElement('canvas');
    this.canvas.width = CANVAS_SIZE;
    this.canvas.height = CANVAS_SIZE;
    this.canvas.style.cssText =
      'display:block;width:100%;height:100%;border-radius:var(--fps-radius-sm,8px);border:1px solid rgba(255,255,255,0.06);box-shadow:inset 0 0 0 1px rgba(46,230,255,0.08)';
    this.root.appendChild(this.canvas);

    const ctx = this.canvas.getContext('2d');
    if (!ctx) throw new Error('FpsMinimap: 2d context unavailable');
    this.ctx = ctx;

    this.poiTooltip = document.createElement('div');
    this.poiTooltip.style.cssText = [
      'position:fixed',
      'z-index:41',
      'pointer-events:none',
      'display:none',
      'padding:4px 8px',
      'border-radius:8px',
      'background:rgba(10,14,24,0.92)',
      'border:1px solid rgba(255,181,74,0.72)',
      'color:#fff',
      'font:700 10px system-ui,Segoe UI,sans-serif',
      'letter-spacing:0.04em',
      'white-space:nowrap',
      'box-shadow:0 6px 18px rgba(0,0,0,0.42)',
    ].join(';');

    this.canvas.addEventListener('click', this.handleCanvasClick);
    this.canvas.addEventListener('mousemove', this.handleCanvasHover);
    this.canvas.addEventListener('mouseleave', this.handleCanvasLeave);
    this.mount.appendChild(this.root);
    this.mount.appendChild(this.poiTooltip);
  }

  setMapClickEnabled(on: boolean): void {
    this.mapClickEnabled = on;
    this.canvas.style.cursor = on ? 'crosshair' : 'default';
    this.root.style.transform = on ? 'scale(1.08)' : 'scale(1)';
    this.root.style.boxShadow = on
      ? '0 8px 32px rgba(0,0,0,0.55),inset 0 1px 0 rgba(255,255,255,0.14),0 0 24px rgba(46,230,255,0.22)'
      : this.baseShadow;
  }

  triggerStormDamagePulse(): void {
    this.stormPulseUntil = performance.now() + 260;
  }

  setOnMapClick(cb: ((x: number, z: number) => void) | undefined): void {
    this.onMapClick = cb;
  }

  canvasToWorld(canvasX: number, canvasY: number): { x: number; z: number } {
    const H = this.worldHalfExtent;
    const innerL = PAD;
    const innerW = CANVAS_SIZE - 2 * PAD;
    const innerH = CANVAS_SIZE - 2 * PAD;
    const nx = (canvasX - innerL) / innerW;
    const nz = 1 - (canvasY - innerL) / innerH;
    return {
      x: nx * 2 * H - H,
      z: nz * 2 * H - H,
    };
  }

  private readonly handleCanvasHover = (ev: MouseEvent): void => {
    const rect = this.canvas.getBoundingClientRect();
    const scaleX = this.canvas.width / rect.width;
    const scaleY = this.canvas.height / rect.height;
    const cx = (ev.clientX - rect.left) * scaleX;
    const cy = (ev.clientY - rect.top) * scaleY;
    const hitRadius = this.mapClickEnabled ? 14 : 11;
    let nearest: { name: string; dist: number } | null = null;
    for (const poi of this.pois) {
      const pt = this.worldToCanvas(poi.x, poi.z);
      const dist = Math.hypot(cx - pt.cx, cy - pt.cy);
      if (dist <= hitRadius && (!nearest || dist < nearest.dist)) {
        nearest = { name: poi.name, dist };
      }
    }
    this.hoveredPoi = nearest?.name ?? null;
    if (this.hoveredPoi) {
      this.poiTooltip.textContent = this.hoveredPoi;
      this.poiTooltip.style.display = 'block';
      this.poiTooltip.style.left = `${ev.clientX + 10}px`;
      this.poiTooltip.style.top = `${ev.clientY - 8}px`;
      this.canvas.style.cursor = 'pointer';
    } else {
      this.poiTooltip.style.display = 'none';
      this.canvas.style.cursor = this.mapClickEnabled ? 'crosshair' : 'default';
    }
  };

  private readonly handleCanvasLeave = (): void => {
    this.hoveredPoi = null;
    this.poiTooltip.style.display = 'none';
    this.canvas.style.cursor = this.mapClickEnabled ? 'crosshair' : 'default';
  };

  private readonly handleCanvasClick = (ev: MouseEvent): void => {
    if (!this.mapClickEnabled || !this.onMapClick) return;
    const rect = this.canvas.getBoundingClientRect();
    const scaleX = this.canvas.width / rect.width;
    const scaleY = this.canvas.height / rect.height;
    const cx = (ev.clientX - rect.left) * scaleX;
    const cy = (ev.clientY - rect.top) * scaleY;
    const { x, z } = this.canvasToWorld(cx, cy);
    this.onMapClick(x, z);
  };

  private worldToCanvas(x: number, z: number): { cx: number; cy: number } {
    const H = this.worldHalfExtent;
    const nx = (x + H) / (2 * H);
    const nz = (z + H) / (2 * H);
    const cx = PAD + nx * (CANVAS_SIZE - 2 * PAD);
    const cy = PAD + (1 - nz) * (CANVAS_SIZE - 2 * PAD);
    return { cx, cy };
  }

  update(
    playerX: number,
    playerZ: number,
    playerYawRad: number,
    opts?: {
      storm?: FpsMinimapStorm;
      markers?: FpsMinimapMarker[];
      busRoute?: FpsMinimapBusRoute;
      heatZones?: FpsMinimapHeatZone[];
      dropMarker?: { x: number; z: number };
      zoomed?: boolean;
    },
  ): void {
    const zoomed = opts?.zoomed ?? this.mapClickEnabled;
    const ctx = this.ctx;
    const pulseLeft = Math.max(0, this.stormPulseUntil - performance.now());
    if (!this.mapClickEnabled) {
      if (pulseLeft > 0) {
        const pulse = pulseLeft / 260;
        this.root.style.transform = `scale(${1 + pulse * 0.045})`;
        this.root.style.boxShadow = `${this.baseShadow},0 0 ${18 + pulse * 30}px rgba(160,80,255,${0.18 + pulse * 0.36})`;
      } else {
        this.root.style.transform = 'scale(1)';
        this.root.style.boxShadow = this.baseShadow;
      }
    }
    const innerL = PAD;
    const innerT = PAD;
    const innerW = CANVAS_SIZE - 2 * PAD;
    const innerH = CANVAS_SIZE - 2 * PAD;

    ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    ctx.fillStyle = 'rgba(8,10,18,0.95)';
    ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

    if (opts?.heatZones?.length) {
      this.drawHeatZones(opts.heatZones);
    }

    if (opts?.busRoute) {
      const br = opts.busRoute;
      const start = this.worldToCanvas(br.startX, br.startZ);
      const end = this.worldToCanvas(br.endX, br.endZ);
      ctx.strokeStyle = 'rgba(255,220,60,0.92)';
      ctx.lineWidth = 2.5;
      ctx.setLineDash([6, 5]);
      ctx.beginPath();
      ctx.moveTo(start.cx, start.cy);
      ctx.lineTo(end.cx, end.cy);
      ctx.stroke();
      ctx.setLineDash([]);

      if (br.busX != null && br.busZ != null) {
        const bus = this.worldToCanvas(br.busX, br.busZ);
        ctx.fillStyle = '#ffe566';
        ctx.strokeStyle = '#1a1400';
        ctx.lineWidth = 1.2;
        ctx.beginPath();
        ctx.arc(bus.cx, bus.cy, 4.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      }
    }

    if (opts?.storm && opts.storm.safeRadius > 0.5) {
      const H = this.worldHalfExtent;
      const { cx, cy } = this.worldToCanvas(opts.storm.cx, opts.storm.cz);
      const stormRadius = opts.storm.stormRadius ?? opts.storm.safeRadius;
      if (stormRadius > opts.storm.safeRadius + 0.5) {
        const spr = (stormRadius / (2 * H)) * innerW;
        ctx.strokeStyle = opts.storm.colorblind
          ? 'rgba(255,170,80,0.58)'
          : 'rgba(190,76,255,0.6)';
        ctx.lineWidth = 2.4;
        ctx.setLineDash([7, 4]);
        ctx.beginPath();
        ctx.arc(cx, cy, Math.max(5, spr), 0, Math.PI * 2);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = opts.storm.colorblind ? 'rgba(255,202,102,0.86)' : 'rgba(224,184,255,0.88)';
        ctx.font = '800 8px system-ui,Segoe UI,sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('STORM', cx, Math.max(10, cy - Math.max(5, spr) - 7));
      }

      const pr = (opts.storm.safeRadius / (2 * H)) * innerW;
      const safePulse = Math.max(0, Math.min(1, opts.storm.safePulse ?? 0));
      if (safePulse > 0.02) {
        const ringPulse = 0.5 + Math.sin(performance.now() * 0.014) * 0.5;
        ctx.save();
        ctx.strokeStyle = opts.storm.colorblind
          ? `rgba(255,202,102,${0.16 + safePulse * 0.34})`
          : `rgba(82,245,255,${0.16 + safePulse * 0.34})`;
        ctx.lineWidth = 2.2 + safePulse * 2.1;
        ctx.shadowColor = opts.storm.colorblind ? 'rgba(255,180,80,0.7)' : 'rgba(82,245,255,0.7)';
        ctx.shadowBlur = 7 + safePulse * (8 + ringPulse * 6);
        ctx.beginPath();
        ctx.arc(cx, cy, Math.max(4, pr) + safePulse * (1.6 + ringPulse * 2.6), 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
      }
      ctx.strokeStyle = opts.storm.colorblind
        ? 'rgba(46,230,255,0.82)'
        : 'rgba(82,245,255,0.82)';
      ctx.lineWidth = 2.2;
      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      ctx.arc(cx, cy, Math.max(4, pr), 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = 'rgba(220,252,255,0.94)';
      ctx.font = '800 8px system-ui,Segoe UI,sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('SAFE', cx, Math.max(10, cy - Math.max(4, pr) + 8));

      const nextR = opts.storm.nextRadius ?? 0;
      if (nextR > 0.5 && opts.storm.nextCx != null && opts.storm.nextCz != null) {
        // Phase 30X-2: next-zone preview ring pulse + NEXT label
        const npr = (nextR / (2 * H)) * innerW;
        const nextPt = this.worldToCanvas(opts.storm.nextCx, opts.storm.nextCz);
        const nextPulse = 0.55 + Math.sin(performance.now() * 0.008) * 0.45;
        ctx.strokeStyle = `rgba(180,255,255,${0.55 + nextPulse * 0.35})`;
        ctx.lineWidth = 1.4 + nextPulse * 0.6;
        ctx.setLineDash([3, 4]);
        ctx.beginPath();
        ctx.arc(nextPt.cx, nextPt.cy, Math.max(3, npr), 0, Math.PI * 2);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = 'rgba(255,255,255,0.92)';
        ctx.strokeStyle = 'rgba(6,12,20,0.9)';
        ctx.lineWidth = 1.2;
        ctx.beginPath();
        ctx.moveTo(nextPt.cx, nextPt.cy - 6);
        ctx.lineTo(nextPt.cx - 5, nextPt.cy + 4);
        ctx.lineTo(nextPt.cx + 5, nextPt.cy + 4);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
        ctx.font = '800 7px system-ui,Segoe UI,sans-serif';
        ctx.fillStyle = `rgba(200,255,255,${0.7 + nextPulse * 0.25})`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('NEXT', nextPt.cx, Math.min(innerW - 8, nextPt.cy + Math.max(6, npr) + 7));

        if (safePulse > 0.02) {
          const playerPt = this.worldToCanvas(playerX, playerZ);
          const dx = nextPt.cx - playerPt.cx;
          const dy = nextPt.cy - playerPt.cy;
          const len = Math.hypot(dx, dy);
          if (len > 6) {
            const ux = dx / len;
            const uy = dy / len;
            const nowPulse = 0.55 + Math.sin(performance.now() * 0.012) * 0.45;
            const a = Math.min(0.94, 0.34 + safePulse * (0.38 + nowPulse * 0.28));
            const startX = playerPt.cx + ux * 10;
            const startY = playerPt.cy + uy * 10;
            const endX = nextPt.cx - ux * Math.max(8, Math.min(18, npr));
            const endY = nextPt.cy - uy * Math.max(8, Math.min(18, npr));
            ctx.save();
            ctx.strokeStyle = opts.storm.colorblind
              ? `rgba(255,202,102,${a})`
              : `rgba(82,245,255,${a})`;
            ctx.shadowColor = opts.storm.colorblind ? 'rgba(255,180,80,0.65)' : 'rgba(82,245,255,0.65)';
            ctx.shadowBlur = 5 + safePulse * 9;
            ctx.lineWidth = 1.8 + safePulse * 1.8;
            ctx.setLineDash([5, 4]);
            ctx.beginPath();
            ctx.moveTo(startX, startY);
            ctx.lineTo(endX, endY);
            ctx.stroke();
            ctx.setLineDash([]);

            const head = 5 + safePulse * 2.2;
            ctx.translate(endX, endY);
            ctx.rotate(Math.atan2(uy, ux));
            ctx.fillStyle = opts.storm.colorblind
              ? `rgba(255,202,102,${Math.min(1, a + 0.12)})`
              : `rgba(180,252,255,${Math.min(1, a + 0.12)})`;
            ctx.beginPath();
            ctx.moveTo(head, 0);
            ctx.lineTo(-head * 0.65, -head * 0.58);
            ctx.lineTo(-head * 0.25, 0);
            ctx.lineTo(-head * 0.65, head * 0.58);
            ctx.closePath();
            ctx.fill();
            ctx.restore();
          }
        }
      }
    }

    if (opts?.markers) {
      ctx.font = '7px system-ui,Segoe UI,sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const pulseT = performance.now() * 0.008;
      for (const m of opts.markers) {
        const { cx, cy } = this.worldToCanvas(m.x, m.z);
        if (m.dead) {
          ctx.fillStyle = '#6a6a72';
          ctx.beginPath();
          ctx.arc(cx, cy, 2.2, 0, Math.PI * 2);
          ctx.fill();
        } else if (m.knocked) {
          const pulse = 0.55 + Math.sin(pulseT) * 0.45;
          ctx.fillStyle = `rgba(255,48,48,${pulse})`;
          ctx.beginPath();
          ctx.arc(cx, cy, 3 + Math.sin(pulseT * 1.35) * 1.2, 0, Math.PI * 2);
          ctx.fill();
        } else {
          let alpha = 1;
          if (m.dot) {
            const dist = Math.hypot(m.x - playerX, m.z - playerZ);
            if (dist >= BOT_DOT_FADE_END) alpha = 0;
            else if (dist > BOT_DOT_FADE_START) {
              alpha = 1 - (dist - BOT_DOT_FADE_START) / (BOT_DOT_FADE_END - BOT_DOT_FADE_START);
            }
          }
          if (alpha <= 0.02) continue;
          ctx.globalAlpha = alpha;
          ctx.fillStyle = m.color;
          ctx.beginPath();
          if (m.dot) {
            ctx.arc(cx, cy, 2.8, 0, Math.PI * 2);
          } else {
            ctx.rect(cx - 2.5, cy - 2.5, 5, 5);
          }
          ctx.fill();
          ctx.globalAlpha = 1;
        }
        if (m.label) {
          ctx.fillStyle = m.dead ? 'rgba(160,160,168,0.75)' : 'rgba(255,255,255,0.75)';
          ctx.fillText(m.label, cx, cy - 7);
        }
      }
    }

    this.trail.push({ x: playerX, z: playerZ });
    if (this.trail.length > this.maxTrail) this.trail.shift();
    const nSkip = 4;
    for (let i = 0; i < this.trail.length - nSkip; i++) {
      const pt = this.trail[i]!;
      const { cx, cy } = this.worldToCanvas(pt.x, pt.z);
      const t = (i + 1) / Math.max(1, this.trail.length - nSkip);
      ctx.fillStyle = `rgba(46,230,255,${t * 0.28})`;
      ctx.beginPath();
      ctx.arc(cx, cy, 1.6 + t * 0.8, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.strokeStyle = 'rgba(46,230,255,0.45)';
    ctx.lineWidth = 1;
    const gridStep = 4;
    for (let i = 1; i < gridStep; i++) {
      const gx = innerL + (innerW * i) / gridStep;
      const gy = innerT + (innerH * i) / gridStep;
      ctx.beginPath();
      ctx.moveTo(gx, innerT);
      ctx.lineTo(gx, innerT + innerH);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(innerL, gy);
      ctx.lineTo(innerL + innerW, gy);
      ctx.stroke();
    }

    ctx.strokeStyle = 'rgba(46,230,255,0.55)';
    ctx.lineWidth = 1.5;
    ctx.strokeRect(innerL + 0.5, innerT + 0.5, innerW - 1, innerH - 1);

    ctx.fillStyle = 'rgba(46,230,255,0.82)';
    ctx.font = '800 7px system-ui,Segoe UI,sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('N', innerL + innerW / 2, innerT + 6);

    const poiFontSize = zoomed ? 10 : 8;
    const poiLabelOffset = zoomed ? 10 : 8;
    ctx.font = `${poiFontSize}px system-ui,Segoe UI,sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    for (const poi of this.pois) {
      const { cx, cy } = this.worldToCanvas(poi.x, poi.z);
      const hovered = this.hoveredPoi === poi.name;
      ctx.fillStyle = hovered ? '#ffd978' : '#ffb54a';
      ctx.beginPath();
      ctx.arc(cx, cy, hovered ? (zoomed ? 4.4 : 3.8) : zoomed ? 3.6 : 3, 0, Math.PI * 2);
      ctx.fill();
      if (hovered) {
        ctx.strokeStyle = 'rgba(255,255,255,0.88)';
        ctx.lineWidth = 1.4;
        ctx.beginPath();
        ctx.arc(cx, cy, (zoomed ? 6.2 : 5.4), 0, Math.PI * 2);
        ctx.stroke();
      }
      const showLabel = hovered || zoomed;
      if (showLabel) {
        ctx.fillStyle = hovered ? '#ffffff' : zoomed ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.85)';
        if (hovered || zoomed) {
          ctx.shadowColor = 'rgba(0,0,0,0.65)';
          ctx.shadowBlur = hovered ? 4 : 3;
        }
        ctx.fillText(poi.name.slice(0, hovered ? 18 : 12), cx, cy - poiLabelOffset - (hovered ? 2 : 0));
        ctx.shadowBlur = 0;
      }
    }

    if (opts?.dropMarker) {
      this.drawDropMarker(opts.dropMarker.x, opts.dropMarker.z);
    }

    // Phase 34X-26: brighter player chevron with outer glow pulse
    const { cx: px, cy: py } = this.worldToCanvas(playerX, playerZ);
    const chevPulse = 0.75 + Math.sin(performance.now() * 0.006) * 0.25;
    ctx.save();
    ctx.translate(px, py);
    ctx.rotate(playerYawRad);
    ctx.shadowColor = `rgba(80,255,255,${0.85 + chevPulse * 0.15})`;
    ctx.shadowBlur = 12 + chevPulse * 6;
    ctx.fillStyle = `rgba(46,230,255,${0.4 + chevPulse * 0.1})`;
    ctx.beginPath();
    ctx.moveTo(0, -12);
    ctx.lineTo(-7, 9);
    ctx.lineTo(7, 9);
    ctx.closePath();
    ctx.fill();
    ctx.shadowBlur = 8;
    ctx.fillStyle = '#6af8ff';
    ctx.strokeStyle = '#041018';
    ctx.lineWidth = 1.35;
    ctx.beginPath();
    ctx.moveTo(0, -10);
    ctx.lineTo(-5.5, 7.5);
    ctx.lineTo(5.5, 7.5);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.strokeStyle = 'rgba(240,255,255,0.98)';
    ctx.lineWidth = 1.8;
    ctx.beginPath();
    ctx.moveTo(0, -5);
    ctx.lineTo(0, -19);
    ctx.stroke();
    ctx.shadowBlur = 0;
    ctx.restore();
  }

  private drawHeatZones(zones: FpsMinimapHeatZone[]): void {
    const ctx = this.ctx;
    for (const zone of zones) {
      const { cx, cy } = this.worldToCanvas(zone.x, zone.z);
      const radius = HEAT_ZONE_RADIUS_PX[zone.tier];
      const color = zone.color ?? LOOT_TIER_COLORS[zone.tier];
      ctx.fillStyle = color;
      ctx.globalAlpha = 0.28;
      ctx.beginPath();
      ctx.arc(cx, cy, radius, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;
    }
  }

  private drawDropMarker(x: number, z: number): void {
    const ctx = this.ctx;
    const { cx, cy } = this.worldToCanvas(x, z);
    const s = 5.5;
    ctx.strokeStyle = '#2ee6ff';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(cx - s, cy - s);
    ctx.lineTo(cx + s, cy + s);
    ctx.moveTo(cx + s, cy - s);
    ctx.lineTo(cx - s, cy + s);
    ctx.stroke();
    ctx.fillStyle = 'rgba(46,230,255,0.85)';
    ctx.beginPath();
    ctx.arc(cx, cy, 2, 0, Math.PI * 2);
    ctx.fill();
  }

  dispose(): void {
    this.canvas.removeEventListener('click', this.handleCanvasClick);
    this.canvas.removeEventListener('mousemove', this.handleCanvasHover);
    this.canvas.removeEventListener('mouseleave', this.handleCanvasLeave);
    this.root.remove();
    this.poiTooltip.remove();
    this.responsiveStyle.remove();
  }
}
